import { useEffect, useRef } from "react";
import { vectorTileRegistry } from "../../apis/VectorTileRegistry";
import { applyLayerOrder } from "../../utils/MapLayerUtils";

const useVectorTile = (
  mapRef,
  layerKey,
  opacity = 1,
  show = false,
  options = {},
) => {
  const isVisibleRef = useRef(show);
  const cleanupTimeoutRef = useRef(null);

  const config = vectorTileRegistry[layerKey];
  const isDynamic = config?.isDynamic;

  // Query string for dynamic tile URLs (countries + departments)
  const buildQueryParams = (opts) => {
    const params = new URLSearchParams();
    opts?.countries?.forEach((c) => params.append("countries", c));
    opts?.departments?.forEach((d) => params.append("departments", d));
    return params.toString();
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!config) {
      console.error(`No config found for vector layer: ${layerKey}`);
      return;
    }

    isVisibleRef.current = show;

    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    const sourceId = config.sourceId || config.id;
    const layers = config.layers || [config.layer];

    const applyLayer = () => {
      if (!show) return;

      // 1. Build tile URL (with query params if dynamic)
      let tilesUrl = config.source.tiles?.[0];
      if (isDynamic && tilesUrl) {
        const queryString = buildQueryParams(options);
        if (queryString) tilesUrl = `${tilesUrl}?${queryString}`;
      }

      // 2. Add or update source
      const existingSource = map.getSource(sourceId);
      if (!existingSource) {
        const sourceConfig = tilesUrl
          ? { ...config.source, tiles: [tilesUrl] }
          : config.source;
        map.addSource(sourceId, sourceConfig);
      } else if (isDynamic && tilesUrl) {
        existingSource.setTiles([tilesUrl]);
      }

      // 3. Load symbol image if needed
      if (config.symbol && !map.hasImage(config.symbol.iconName)) {
        map.loadImage(config.symbol.iconUrl, (error, image) => {
          if (error) return;
          if (!map.hasImage(config.symbol.iconName)) {
            map.addImage(config.symbol.iconName, image);
          }
        });
      }

      // 4. Add or show layers
      layers.forEach((layerConfig) => {
        const layerId = layerConfig.id;
        if (!map.getLayer(layerId)) {
          const fullLayerConfig = {
            ...layerConfig,
            source: sourceId,
            "source-layer":
              layerConfig["source-layer"] || layerConfig.sourceLayer,
            layout: { ...layerConfig.layout, visibility: "visible" },
          };
          if (fullLayerConfig.sourceLayer) delete fullLayerConfig.sourceLayer;

          if (layerConfig.type === "line") {
            fullLayerConfig.paint = {
              ...layerConfig.paint,
              "line-opacity": opacity,
            };
          } else if (layerConfig.type === "fill") {
            fullLayerConfig.paint = {
              ...layerConfig.paint,
              "fill-opacity": opacity,
            };
          } else if (layerConfig.type === "symbol") {
            fullLayerConfig.paint = {
              ...layerConfig.paint,
              "icon-opacity": opacity,
            };
          }

          map.addLayer(fullLayerConfig);
        } else {
          map.setLayoutProperty(layerId, "visibility", "visible");
          if (layerConfig.type === "line")
            map.setPaintProperty(layerId, "line-opacity", opacity);
          else if (layerConfig.type === "fill")
            map.setPaintProperty(layerId, "fill-opacity", opacity);
          else if (layerConfig.type === "symbol")
            map.setPaintProperty(layerId, "icon-opacity", opacity);
        }
      });

      // 5. Layer ordering
      if (options.priorityLayers) {
        options.priorityLayers.forEach((priorityLayerId) => {
          if (map.getLayer(priorityLayerId)) {
            applyLayerOrder(map);
          }
        });
      }
    };

    if (show) {
      applyLayer();
    } else {
      layers.forEach((layerConfig) => {
        if (map.getLayer(layerConfig.id)) {
          map.setLayoutProperty(layerConfig.id, "visibility", "none");
        }
      });

      cleanupTimeoutRef.current = setTimeout(() => {
        if (!isVisibleRef.current) {
          layers.forEach((layerConfig) => {
            if (map.getLayer(layerConfig.id)) map.removeLayer(layerConfig.id);
          });
          if (map.getSource(sourceId)) map.removeSource(sourceId);
          if (config.symbol && map.hasImage(config.symbol.iconName)) {
            map.removeImage(config.symbol.iconName);
          }
        }
      }, 5000);
    }

    map.on("style.load", applyLayer);

    return () => {
      if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
      map.off("style.load", applyLayer);
    };
  }, [
    mapRef,
    layerKey,
    show,
    ...(isDynamic ? [options.countries, options.departments] : []),
  ]);

  return null;
};

export default useVectorTile;
